import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { InlineNotification, Tile } from "@carbon/react";
import StatCard from "../../components/carbon/StatCard/StatCard";
import DataTable, {
  type DataTableColumn,
} from "../../components/carbon/DataTable/DataTable";
import type { ImportPreview } from "../../types";
import { describeIssue, describeWarning } from "./importMessages";

type Props = { preview: ImportPreview };

type ProvenanceRow = {
  id: string;
  field: ReactNode;
  mapped: number;
  defaulted: number;
  nulled: number;
  converted: number;
  unreadable: ReactNode;
};

const PROVENANCE_COLUMNS = [
  "field",
  "mapped",
  "defaulted",
  "nulled",
  "converted",
  "unreadable",
] as const;

/**
 * How the file was read: the raw counters, where each field came from, what was converted, and
 * the rows that read oddly but still import.
 *
 * None of it is a decision the person importing has to take, so it sits behind "see how the file
 * was read" for whoever wants to check the reading. Rows that will be left out are not here: they
 * change what gets created, so the summary names them.
 */
const ImportTechnicalDetails = ({ preview }: Props) => {
  const { t, i18n } = useTranslation();
  const exists = (key: string) => i18n.exists(key);

  const tiles: Array<[string, number]> = [
    ["sheets", preview.sheetCount],
    ["rowsRead", preview.rowsRead],
    ["rowsSkipped", preview.rowsSkipped],
    ["groups", preview.groupCount],
    ["items", preview.itemCount],
  ];

  const oddRows = preview.issues.filter((issue) => issue.severity !== "ERROR");

  const provenanceHeaders: DataTableColumn<ProvenanceRow>[] =
    PROVENANCE_COLUMNS.map((key) => ({
      key,
      header: t(`purchaseRequests.import.table.${key}`),
    }));

  const provenanceRows: ProvenanceRow[] = preview.fields.map((field) => ({
    id: field.target,
    field: <code className="pr-import__code">{field.target}</code>,
    mapped: field.mapped,
    defaulted: field.defaulted,
    nulled: field.nulled,
    converted: field.coerced,
    unreadable:
      field.invalid > 0 ? (
        <span className="pr-import__row-error">{field.invalid}</span>
      ) : (
        field.invalid
      ),
  }));

  return (
    <>
      <div className="pr-import__stats">
        {tiles.map(([key, value]) => (
          <StatCard
            key={key}
            value={value}
            label={t(`purchaseRequests.import.tiles.${key}`)}
          />
        ))}
      </div>

      <Tile className="pr-import__panel pr-import__panel--flush">
        <div className="pr-import__panel-head">
          <h3 className="pr-import__title">
            {t("purchaseRequests.import.provenance")}
          </h3>
          <p className="pr-import__hint">
            {t("purchaseRequests.import.provenanceHint")}
          </p>
        </div>
        <DataTable
          headers={provenanceHeaders}
          rows={provenanceRows}
          isLoading={false}
          empty={
            <p className="pr-import__hint">
              {t("purchaseRequests.import.noFields")}
            </p>
          }
        />
      </Tile>

      {preview.coercions.length > 0 && (
        <Tile className="pr-import__panel">
          <h3 className="pr-import__title">
            {t("purchaseRequests.import.coercions")}
          </h3>
          <ul className="pr-import__plain-list">
            {preview.coercions.map((coercion) => (
              <li key={`${coercion.column}-${coercion.toType}`}>
                <strong>{coercion.column}</strong>{" "}
                <span className="pr-import__muted">
                  {/* A value can change without its type changing — EA becoming UN. Saying
                      "text to text" for that reads like a mistake rather than a translation. */}
                  {coercion.fromType === coercion.toType
                    ? t("purchaseRequests.import.normalised")
                    : t("purchaseRequests.import.convertedFromTo", {
                        from: coercion.fromType,
                        to: coercion.toType,
                      })}
                </span>{" "}
                <span className="pr-import__muted">({coercion.rowCount})</span>
              </li>
            ))}
          </ul>
        </Tile>
      )}

      {preview.warnings.length > 0 && (
        <InlineNotification
          kind="info"
          lowContrast
          hideCloseButton
          className="pr-import__notification"
          title={t("purchaseRequests.import.warnings")}
        >
          <ul className="pr-import__notification-list">
            {preview.warnings.map((warning) => (
              <li key={`${warning.code}-${warning.subject}`}>
                {describeWarning(warning, t, exists)}
              </li>
            ))}
          </ul>
        </InlineNotification>
      )}

      {oddRows.length > 0 && (
        <Tile className="pr-import__panel">
          <h3 className="pr-import__title">
            {t("purchaseRequests.import.rowIssues")}
          </h3>
          <ul className="pr-import__plain-list pr-import__plain-list--scroll">
            {oddRows.slice(0, 50).map((issue, index) => (
              <li key={`${issue.rowNumber}-${issue.column}-${index}`}>
                <span className="pr-import__muted">
                  {t("purchaseRequests.import.rowLabel", {
                    row: issue.rowNumber,
                  })}
                </span>{" "}
                {describeIssue(issue, t, exists)}
              </li>
            ))}
          </ul>
        </Tile>
      )}
    </>
  );
};

export default ImportTechnicalDetails;
